/**
 * IC Referral API Service
 * Handles contractor code validation and lead submission
 * 
 * NOTE: This service connects to the IC Contractor API which is hosted
 * on app.kgob.com (production) or a separate preview environment (dev).
 * The IC API is NOT part of this kgob.com marketing site - it's an external service.
 */

// Get API base URL based on environment
// Uses REACT_APP_IC_API_URL if set, otherwise determines from hostname
const getApiBaseUrl = () => {
  // Allow override via environment variable
  if (process.env.REACT_APP_IC_API_URL) {
    return process.env.REACT_APP_IC_API_URL;
  }
  
  const hostname = window.location.hostname;
  
  // Development/Preview environments - use the IC system preview
  if (hostname === 'localhost' || 
      hostname.includes('preview.emergentagent.com') ||
      hostname.includes('127.0.0.1')) {
    // External IC API on separate preview environment (same host as this app's backend in preview)
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Production - use app.kgob.com IC API
  return 'https://app.kgob.com';
};

/**
 * Validate a contractor referral code
 * @param {string} code - The contractor code (e.g., KGOB-IC-6U69KA)
 * @returns {Promise<{valid: boolean, contractor_name?: string, message?: string}>}
 */
export const validateContractorCode = async (code) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/api/v1/contractor/public/validate/${encodeURIComponent(code)}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
      }
    );
    
    const data = await response.json();
    
    if (response.ok && data.valid) {
      return {
        valid: true,
        contractor_name: data.contractor_name || 'Your Referrer'
      };
    }
    
    return {
      valid: false,
      message: data.message || 'Invalid referral code'
    };
  } catch (error) {
    console.error('Contractor code validation error:', error);
    return {
      valid: false,
      message: 'Unable to validate referral code. Please try again.'
    };
  }
};

/**
 * Submit a referral lead
 * @param {Object} leadData - The lead information
 * @param {string} leadData.contractor_code - The contractor's referral code
 * @param {string} leadData.name - Prospect's full name
 * @param {string} leadData.email - Prospect's email
 * @param {string} leadData.phone - Prospect's phone number
 * @param {string} [leadData.address] - Optional address/city
 * @param {string} [leadData.notes] - Optional notes (best time to contact)
 * @returns {Promise<{success: boolean, message: string, lead_id?: string, warning?: string}>}
 */
export const submitReferralLead = async (leadData) => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(
      `${baseUrl}/api/v1/contractor/public/lead`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          contractor_code: leadData.contractor_code,
          name: leadData.name,
          email: leadData.email,
          phone: leadData.phone,
          address: leadData.address || '',
          notes: leadData.notes || '',
          source: 'landing_page'
        })
      }
    );
    
    const data = await response.json();
    
    if (response.ok && data.success) {
      return {
        success: true,
        message: data.message || 'Thank you! A KGOB representative will contact you shortly.',
        lead_id: data.lead_id,
        warning: data.warning,
        onboard_url: data.onboard_url,
        redirect_url: data.redirect_url
      };
    }
    
    // Handle validation errors (422)
    if (response.status === 422 && data.detail) {
      const errorMessages = Array.isArray(data.detail) 
        ? (data.detail || []).map(e => e.msg).join(', ')
        : data.detail;
      return {
        success: false,
        message: errorMessages || 'Please check your information and try again.'
      };
    }
    
    // Handle other errors
    return {
      success: false,
      message: data.detail || data.message || 'Submission failed. Please try again.'
    };
  } catch (error) {
    console.error('Lead submission error:', error);
    return {
      success: false,
      message: 'Network error. Please check your connection and try again.'
    };
  }
};

export default {
  validateContractorCode,
  submitReferralLead
};
